import React, { useState, useEffect } from 'react';
import {
  Search,
  AlertTriangle,
  AlertCircle,
  ShieldAlert,
  UserX,
  Clock,
  CheckCircle,
  Eye,
  MapPin,
  Calendar,
  User,
  Filter,
  ChevronDown,
  ChevronUp,
  ImageIcon,
  MessageSquare,
  Activity,
  Mic,
  Play,
  Pause
} from 'lucide-react';
import api from '../../services/apiService';

const TYPE_CONFIG = {
  accident: {
    label: 'Accident',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertTriangle,
    iconColor: 'text-red-600'
  },
  near_miss: {
    label: 'Near Miss',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: AlertCircle,
    iconColor: 'text-orange-600'
  },
  unsafe_condition: {
    label: 'Unsafe Condition',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: ShieldAlert,
    iconColor: 'text-amber-600'
  },
  unsafe_act: {
    label: 'Unsafe Act',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: UserX,
    iconColor: 'text-purple-600'
  },
};

const SEVERITY_CONFIG = {
  low: { label: 'Low', color: 'bg-green-100 text-green-800' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-800' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-800' },
};

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Clock },
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700', icon: Clock },
  under_review: { label: 'Under Review', color: 'bg-yellow-100 text-yellow-700', icon: Eye },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-500', icon: CheckCircle },
};

const IncidentList = () => {
  const [incidents, setIncidents] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [audioRef, setAudioRef] = useState(null);

  useEffect(() => {
    loadData();
  }, [typeFilter, severityFilter, statusFilter]);

  // Cleanup audio when component unmounts
  useEffect(() => {
    return () => {
      if (audioRef) {
        audioRef.pause();
      }
    };
  }, [audioRef]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (severityFilter) params.severity = severityFilter;
      if (statusFilter) params.status = statusFilter;

      const [incidentsRes, statsRes] = await Promise.all([
        api.get('/incidents/', { params }),
        api.get('/incidents/statistics/')
      ]);

      setIncidents(incidentsRes.data);
      setStatistics(statsRes.data);
    } catch (err) {
      console.error('Failed to load incidents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await api.post(`/incidents/${id}/update_status/`, { status: newStatus });
      setIncidents(incidents.map(inc =>
        inc.id === id ? response.data : inc
      ));
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const filteredIncidents = incidents.filter(incident => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      incident.title?.toLowerCase().includes(term) ||
      incident.description?.toLowerCase().includes(term) ||
      incident.location?.toLowerCase().includes(term) ||
      incident.reporter_name?.toLowerCase().includes(term)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAudioPlay = (audioId, audioUrl) => {
    // Stop currently playing audio
    if (audioRef) {
      audioRef.pause();
      audioRef.currentTime = 0;
    }

    if (playingAudioId === audioId) {
      // Toggle off if same audio
      setPlayingAudioId(null);
      setAudioRef(null);
    } else {
      // Play new audio
      const audio = new Audio(audioUrl);
      audio.onended = () => {
        setPlayingAudioId(null);
        setAudioRef(null);
      };
      audio.play();
      setPlayingAudioId(audioId);
      setAudioRef(audio);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="h-7 w-7 text-red-600" />
          Incident Reports
        </h1>
        <p className="text-gray-600 mt-1">
          View and manage safety incident reports from the mobile app
        </p>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
            <div className="text-2xl font-bold text-gray-900">{statistics.total}</div>
            <div className="text-sm text-gray-600">Total Incidents</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
            <div className="text-2xl font-bold text-red-600">{statistics.by_type?.accident || 0}</div>
            <div className="text-sm text-gray-600">Accidents</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
            <div className="text-2xl font-bold text-orange-600">{statistics.by_type?.near_miss || 0}</div>
            <div className="text-sm text-gray-600">Near Misses</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
            <div className="text-2xl font-bold text-amber-600">
              {(statistics.by_type?.unsafe_condition || 0) + (statistics.by_type?.unsafe_act || 0)}
            </div>
            <div className="text-sm text-gray-600">Unsafe Conditions/Acts</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-pink-500">
            <div className="text-2xl font-bold text-pink-600">{statistics.with_injuries || 0}</div>
            <div className="text-sm text-gray-600">With Injuries</div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search incidents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              <Filter className="h-5 w-5" />
              Filters
              {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Severities</option>
                  {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Incidents List */}
        <div className="divide-y">
          {filteredIncidents.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No incidents found</p>
            </div>
          ) : (
            filteredIncidents.map((incident) => {
              const typeConfig = TYPE_CONFIG[incident.type] || TYPE_CONFIG.near_miss;
              const severityConfig = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.medium;
              const statusConfig = STATUS_CONFIG[incident.status] || STATUS_CONFIG.submitted;
              const TypeIcon = typeConfig.icon;
              const StatusIcon = statusConfig.icon;
              const isExpanded = expandedId === incident.id;

              return (
                <div key={incident.id} className="hover:bg-gray-50">
                  {/* Main Row */}
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : incident.id)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Type Icon */}
                      <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                        <TypeIcon className={`h-6 w-6 ${typeConfig.iconColor}`} />
                      </div>

                      {/* Main Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-gray-900 truncate">
                              {incident.title}
                            </h3>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {incident.description}
                            </p>
                          </div>

                          {/* Badges */}
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${severityConfig.color}`}>
                              {severityConfig.label}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                              <StatusIcon className="h-3 w-3" />
                              {statusConfig.label}
                            </span>
                          </div>
                        </div>

                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(incident.occurred_at)}
                          </span>
                          {incident.reporter_name && (
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              {incident.reporter_name}
                            </span>
                          )}
                          {incident.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {incident.location}
                            </span>
                          )}
                          {incident.injury_involved && (
                            <span className="flex items-center gap-1 text-red-600 font-medium">
                              <Activity className="h-4 w-4" />
                              Injury reported
                            </span>
                          )}
                          {incident.photos_count > 0 && (
                            <span className="flex items-center gap-1">
                              <ImageIcon className="h-4 w-4" />
                              {incident.photos_count} photo(s)
                            </span>
                          )}
                          {incident.audio_files && incident.audio_files.length > 0 && (
                            <span className="flex items-center gap-1 text-purple-600">
                              <Mic className="h-4 w-4" />
                              {incident.audio_files.length} voice note(s)
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Expand Arrow */}
                      <div className="flex-shrink-0">
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-gray-50 border-t">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                        {/* Left Column - Details */}
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Full Description</h4>
                            <p className="text-sm text-gray-600 bg-white p-3 rounded border">
                              {incident.description}
                            </p>
                          </div>

                          {incident.immediate_actions && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Immediate Actions Taken</h4>
                              <p className="text-sm text-gray-600 bg-white p-3 rounded border">
                                {incident.immediate_actions}
                              </p>
                            </div>
                          )}

                          {incident.witnesses && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Witnesses</h4>
                              <p className="text-sm text-gray-600 bg-white p-3 rounded border">
                                {incident.witnesses}
                              </p>
                            </div>
                          )}

                          {incident.injury_involved && (
                            <div className="bg-red-50 border border-red-200 rounded p-3">
                              <h4 className="text-sm font-medium text-red-800 mb-1">Injury Information</h4>
                              <p className="text-sm text-red-700">
                                {incident.injured_count || 1} person(s) injured
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Right Column - Location & Actions */}
                        <div className="space-y-4">
                          {/* Location Info */}
                          <div className="bg-white p-4 rounded border">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Location Details</h4>
                            <div className="space-y-2 text-sm">
                              {incident.location && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Location:</span>
                                  <span className="text-gray-900">{incident.location}</span>
                                </div>
                              )}
                              {incident.turbine_id && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Turbine ID:</span>
                                  <span className="text-gray-900">{incident.turbine_id}</span>
                                </div>
                              )}
                              {incident.project_name && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Project:</span>
                                  <span className="text-gray-900">{incident.project_name}</span>
                                </div>
                              )}
                              {incident.latitude && incident.longitude && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">GPS:</span>
                                  <a
                                    href={`https://maps.google.com/?q=${incident.latitude},${incident.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    View on Map
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Status Change */}
                          <div className="bg-white p-4 rounded border">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Update Status</h4>
                            <select
                              value={incident.status}
                              onChange={(e) => handleStatusChange(incident.id, e.target.value)}
                              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                            >
                              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                <option key={key} value={key}>{config.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Timeline */}
                          <div className="bg-white p-4 rounded border">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Timeline</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Occurred:</span>
                                <span className="text-gray-900">{formatDate(incident.occurred_at)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Reported:</span>
                                <span className="text-gray-900">{formatDate(incident.reported_at)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Photos */}
                      {incident.photos && incident.photos.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Photos</h4>
                          <div className="flex gap-3 overflow-x-auto pb-2">
                            {incident.photos.map((photo, idx) => (
                              <a
                                key={idx}
                                href={photo.image}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0"
                              >
                                <img
                                  src={photo.image}
                                  alt={`Incident photo ${idx + 1}`}
                                  className="h-24 w-24 object-cover rounded-lg border hover:opacity-80 transition-opacity"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Audio Files / Voice Notes */}
                      {incident.audio_files && incident.audio_files.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                            <Mic className="h-4 w-4 text-purple-600" />
                            Voice Notes
                          </h4>
                          <div className="flex flex-wrap gap-3">
                            {incident.audio_files.map((audio, idx) => {
                              const audioId = `${incident.id}-${idx}`;
                              const isPlaying = playingAudioId === audioId;

                              return (
                                <div
                                  key={idx}
                                  className="flex items-center gap-3 bg-white border rounded-lg px-4 py-3 hover:bg-gray-50 transition-colors"
                                >
                                  <button
                                    onClick={() => handleAudioPlay(audioId, audio.audio)}
                                    className={`p-2 rounded-full transition-colors ${
                                      isPlaying
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                                    }`}
                                  >
                                    {isPlaying ? (
                                      <Pause className="h-5 w-5" />
                                    ) : (
                                      <Play className="h-5 w-5" />
                                    )}
                                  </button>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      Voice Note {idx + 1}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {audio.duration_seconds
                                        ? formatDuration(audio.duration_seconds)
                                        : 'Audio recording'}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default IncidentList;
